import {
  FileSignature,
  Receipt,
  CreditCard,
  ShieldCheck,
} from "lucide-react";
import { Link } from "react-router-dom";

const items = [
  {
    icon: FileSignature,
    title: "Statement of Work",
    description:
      "Every engagement begins with a written Statement of Work. Scope, deliverables, timeline and total fees are documented before any sourcing work commences.",
  },
  {
    icon: Receipt,
    title: "Fee Structures",
    description:
      "Engagements are priced as retainers, hourly consulting fees, or intermediation commissions — whichever best fits the scope. All fees are quoted in US Dollars (USD).",
  },
  {
    icon: CreditCard,
    title: "Bank Invoicing & Wire",
    description:
      "Invoices are issued and settled via secure bank invoicing and wire transfer in US Dollars. Card payment via Stripe is available as a secondary option upon request. No fees are collected before a Statement of Work is signed.",
  },
];

export default function EngagementModel() {
  return (
    <section
      id="engagement"
      data-testid="engagement-section"
      className="bg-white py-20 lg:py-32 border-t border-slate-200"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <div className="eyebrow">Engagement Model</div>
          <h2 className="font-display mt-4 text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-slate-900">
            Transparent <span className="font-semibold">B2B billing.</span>
          </h2>
          <p className="mt-5 text-[15px] text-slate-600 leading-relaxed max-w-md">
            We do not list public retail prices because every cross-border
            sourcing engagement is scoped individually. Fees, deliverables
            and timelines are formalized in writing before any work begins
            — and only after a signed Statement of Work do we issue a
            bank-invoice in USD.
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <Link
              to="/terms-of-service"
              data-testid="engagement-tos-link"
              className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.18em] font-bold text-slate-700 hover:text-slate-900 border-b border-slate-300 hover:border-slate-900 pb-0.5"
            >
              <ShieldCheck size={13} /> Terms of Service
            </Link>
            <Link
              to="/refund-policy"
              data-testid="engagement-refund-link"
              className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.18em] font-bold text-slate-700 hover:text-slate-900 border-b border-slate-300 hover:border-slate-900 pb-0.5"
            >
              <ShieldCheck size={13} /> Refund &amp; Delivery
            </Link>
          </div>
        </div>

        <div className="lg:col-span-8 grid sm:grid-cols-3 gap-6">
          {items.map((it) => {
            const Icon = it.icon;
            return (
              <article
                key={it.title}
                data-testid={`engagement-${it.title
                  .toLowerCase()
                  .replace(/[^a-z0-9]+/g, "-")
                  .replace(/^-|-$/g, "")}`}
                className="bg-slate-50/60 border border-slate-200 p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_40px_-12px_rgba(15,23,42,0.18)] hover:border-slate-300"
              >
                <div className="h-10 w-10 inline-flex items-center justify-center bg-slate-900 text-white rounded-sm">
                  <Icon size={18} />
                </div>
                <h3 className="font-display mt-6 text-lg font-semibold tracking-tight text-slate-900">
                  {it.title}
                </h3>
                <p className="mt-3 text-[14px] text-slate-600 leading-relaxed">
                  {it.description}
                </p>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
